import cx from 'classnames';
import {type ChangeEventHandler, type FormEvent, memo, useCallback, useState} from 'react';
import {createPortal} from 'react-dom';
import {getPydanticError} from '~/common/utils/errors';
import {usePostsApi} from '~/ViewInvitation/components/Guestlist/hooks/usePostsApi';
import type {Post as PostType, ReplyResponse, Reply as ReplyType} from '~/ViewInvitation/types/api';
import {Button} from '~library/atoms/Button';
import {GenericSpinner} from '~library/atoms/GenericSpinner';
import {Input} from '~library/atoms/Input';
import {Typography} from '~library/atoms/Typography';
import {useIsMediumAndUp} from '~library/hooks/useMatchQuery';
import {XIcon} from '~library/svg/v2/XIcon';

import * as styles from './ReplyForm.module.scss';

const findParentName = (posts: PostType[], parentId: number) => {
  return posts?.find((p) => p.id === parentId)?.creator?.name || '';
};

interface ReplyFormProps {
  onReply: (comment: ReplyResponse | Error) => void;
  post: ReplyType | PostType;
  closeEditor?: () => void;
  commentText?: string;
}

interface FormElements extends HTMLFormControlsCollection {
  reply: HTMLInputElement;
}

interface ReplyFormElement extends HTMLFormElement {
  elements: FormElements;
}

export const ReplyForm = memo(({onReply, post, closeEditor, commentText = ''}: ReplyFormProps) => {
  const isMediumAndUp = useIsMediumAndUp();
  const {posts} = usePostsApi();
  const postId = post.id;
  const creatorName = post.parentId ? findParentName(posts, post.parentId) : post.creator?.name;
  const {
    reply,
    editComment,
    status: {reply: replyStatus, editComment: editCommentStatus},
  } = usePostsApi();
  const isLoading = replyStatus.isLoading || editCommentStatus.isLoading;
  const [newComment, setNewComment] = useState(commentText);
  const [formError, setFormError] = useState<Error | string | null>(null);

  const onChange: ChangeEventHandler<HTMLTextAreaElement> = useCallback((e) => {
    setNewComment(e.target.value);
  }, []);

  const doReply = useCallback(
    async (e: FormEvent<ReplyFormElement>) => {
      e.preventDefault();
      const value = e.currentTarget.elements.reply.value;
      try {
        const fn = commentText ? editComment : reply;
        const response = await fn({postId, comment: value});
        onReply(response);
      } catch (e) {
        evite.error('Failed to reply to post', e);
        setFormError(e as Error);
      }
    },
    [postId, reply, onReply, editComment, commentText]
  );

  const errorMessage = formError
    ? getPydanticError(formError, 'An error occurred, please try again or contact support')
    : '';

  const form = (
    <form
      onSubmit={doReply}
      className={cx(styles.form, {
        [styles.formAbsolute]: !isMediumAndUp,
      })}
    >
      {isMediumAndUp || (
        <div className={styles.formAbsoluteHeader}>
          <Typography variant="list1" size="large">
            Replying to <strong>{creatorName}</strong>
          </Typography>
          <Button data-qa-id="close-mobile-editor" onClick={closeEditor} variant="transparent">
            <XIcon ratio={0.75} />
          </Button>
        </div>
      )}
      <Input
        className={styles.comment}
        type="textarea"
        data-qa-id="reply-comment-input"
        name="reply"
        value={newComment}
        onChange={onChange}
        placeholder="Add a comment"
        maxLength={500}
        error={errorMessage}
        title={newComment.length === 0 ? 'Please enter a comment' : ''}
        autoFocus
      />
      <Button
        data-qa-id="reply-commen-submit"
        type="submit"
        variant="primary"
        size="large"
        className={styles.submitButton}
        disabled={replyStatus.isLoading || newComment.length === 0}
        formSubmit
      >
        {isLoading ? <GenericSpinner size="1rem" /> : 'Post'}
      </Button>
    </form>
  );

  return isMediumAndUp
    ? form
    : createPortal(form, document.getElementById('guest-summary-bottom') as HTMLElement);
});

ReplyForm.displayName = 'ReplyForm';
